import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useMutation } from '@tanstack/react-query';
import { useLocale } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import {
  sendSupportMessage,
  type SupportCategory,
  type SupportMessageInput,
  type SupportTopic,
} from '../lib/support-client';
import { COMPANY, HAS_SUPPORT_TELEGRAM, HAS_TELEGRAM } from '../lib/company';
import { useSwipeDownToDismiss } from '../lib/use-swipe-down-to-dismiss';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { Select } from './ui/select';

// The alerts bot, offered as a secondary contact next to the personal handle.
const TG_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;

const CATEGORIES: SupportCategory[] = [
  'improvement',
  'bug',
  'subscription',
  'notification',
  'other',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Помощь и поддержка" — a self-contained trigger button + modal. Clicking the
 * trigger opens an accessible dialog (mobile bottom-sheet / desktop centered),
 * reusing the a11y pattern from OnboardingModal (Escape-to-close, focus trap,
 * scroll-lock, backdrop-press-origin guard). The form emails a categorized
 * message to our AI CRM intake via POST /support.
 *
 * The trigger is styleable via `className`/`children` so the SAME component
 * drops into both the sidebar footer and the dashboard.
 */
export function ContactSupport({
  className,
  children,
  defaultCategory = 'improvement',
  topic,
}: {
  className?: string;
  children?: ReactNode;
  /** Category the modal opens on. Lets a context-specific trigger (e.g. a
   *  «не получается оплатить» button on checkout) pre-tag the intake instead of
   *  defaulting to «improvement». */
  defaultCategory?: SupportCategory;
  /**
   * A pre-set subject — `company_data` for «Это ваша компания? Скрыть телефон
   * или исправить данные» (owner, 2026-10-07). The form opens on «Другое»
   * with the message prefilled «Данные компании ИНН <tin>», shows the topic
   * line, and sends `topic` + `tin` so the ticket names the company even if
   * the sender rewrites the text.
   */
  topic?: { kind: SupportTopic; tin: string };
}) {
  const { t } = useLocale();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // A signed-in user with a REAL account inbox: the team can just reply to it,
  // so they needn't retype it. Telegram-native users get a synthetic
  // `telegram_<id>@users.uztender.com` address that bounces — treat them like
  // guests and ask for a real reply address.
  const canReplyToAccount =
    !!user?.email && !/^telegram_\d+@users\.uztender\.com$/i.test(user.email);

  const [category, setCategory] = useState<SupportCategory>(defaultCategory);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const overlayPress = useRef(false);
  const titleId = 'support-title';
  const categoryLabelId = 'support-category-label';

  const mutation = useMutation({
    mutationFn: (input: SupportMessageInput) => sendSupportMessage(input),
  });

  const close = useCallback(() => setOpen(false), []);

  // Swipe the mobile bottom sheet down to dismiss. `setPanelRef` is stable and
  // fans the node out to BOTH the focus/scroll ref and the gesture ref.
  const swipe = useSwipeDownToDismiss(close, open);
  // Keyboard-safe sheet height — see the hook for the full iOS story.
  useVisualViewportSheetHeight(open);
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      swipe.ref.current = node;
    },
    [swipe.ref],
  );

  // Prefill the email from the signed-in user each time the modal opens, and
  // reset the form/mutation so a reopen starts clean.
  const openModal = useCallback(() => {
    setEmail(canReplyToAccount ? (user?.email ?? '') : '');
    setCategory(topic ? 'other' : defaultCategory);
    setMessage(topic ? t('support.companyData.prefill', { tin: topic.tin }) : '');
    setTouched(false);
    mutation.reset();
    setOpen(true);
  }, [canReplyToAccount, user?.email, mutation, defaultCategory, topic, t]);

  // Scroll-lock the page behind the modal.
  // Page scroll behind the modal is locked by the `<RemoveScroll>` wrapper on
  // the modal (iOS-correct, matches Radix Sheet) — see card-save-modal.

  // a11y: move focus in, restore on close, Escape-to-close, trap Tab.
  useEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    const prevFocus = document.activeElement as HTMLElement | null;
    el?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !el) return;
      const f = el.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prevFocus?.focus?.();
    };
  }, [open, close]);

  const trimmedMessage = message.trim();
  const trimmedEmail = email.trim();
  const valid =
    CATEGORIES.includes(category) && trimmedMessage.length >= 5 && EMAIL_RE.test(trimmedEmail);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid || mutation.isPending) return;
    mutation.mutate({
      category,
      message: trimmedMessage,
      email: trimmedEmail,
      name: user?.name ?? undefined,
      ...(topic ? { topic: topic.kind, tin: topic.tin } : {}),
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={
          className ??
          'inline-flex items-center gap-1.5 text-[12.5px] font-medium text-ink-muted transition-colors hover:text-ink'
        }
      >
        {children ?? t('support.trigger')}
      </button>

      {open && (
        <RemoveScroll>
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 md:items-center md:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onPointerDown={(e) => {
              overlayPress.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget && overlayPress.current) close();
            }}
          >
            <div
              ref={setPanelRef}
              tabIndex={-1}
              /* `92vh` + the `supports-[height:1dvh]` twin, not a lone `dvh`
                 (2026-09-26): `dvh` shipped in Chrome 108, and on Chrome 101 a
                 `dvh` inside `max-height` computes to `none` — no cap at all —
                 while `--sheet-lift` still applies. See the invoice sheet in
                 `routes/plans.tsx` for the measurement. */
              className="relative mb-[var(--sheet-lift,0px)] max-h-[var(--sheet-max,92vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)] w-full overflow-y-auto overscroll-contain md:mb-0 rounded-t-2xl border border-edge bg-bone-surface p-5 shadow-xl outline-none md:max-w-md md:rounded-xl md:p-6"
              style={swipe.style}
              onClick={(e) => e.stopPropagation()}
              {...swipe.handlers}
            >
              {/* Bottom-sheet drag handle (mobile only) — see card-save-modal. */}
              <div className="-mt-1 mb-3 flex justify-center md:hidden">
                <span aria-hidden="true" className="h-1 w-10 rounded-full bg-charcoal/30" />
              </div>
              <button
                type="button"
                onClick={swipe.dismiss}
                aria-label={t('support.close')}
                className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-md text-[15px] text-ink-muted transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone hover:text-ink"
              >
                ✕
              </button>

              <h2
                id={titleId}
                className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
              >
                {t('support.title')}
              </h2>
              <p className="mb-2 text-[13px] leading-[1.55] text-ink-soft">{t('support.desc')}</p>
              {/* This screen promises «мы свяжемся с вами по email» and asks for an
                «Email для ответа», so withholding our own address read as evasive.
                It was withheld because COMPANY.email was a personal working inbox;
                it is now the support group, so there is nothing to hide. */}
              <p className="mb-2 text-[13px] leading-[1.55] text-ink-soft">
                {t('support.emailPrefix')}{' '}
                <a
                  href={COMPANY.emailUrl}
                  className="font-semibold text-brand underline-offset-2 hover:underline"
                >
                  {COMPANY.email}
                </a>
              </p>
              {/* The human Telegram contact follows the published-contact flag, not
                the bot flag: Kazakhstan has no bot and still lists Telegram + email
                (owner, 2026-10-13). The bot link next to it stays bot-only. */}
              {HAS_SUPPORT_TELEGRAM && (
                <p className="mb-4 text-[13px] leading-[1.55] text-ink-soft">
                  {t('support.telegramPrefix')}{' '}
                  <a
                    href={COMPANY.supportTelegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand underline-offset-2 hover:underline"
                  >
                    {COMPANY.supportTelegram}
                  </a>
                  {HAS_TELEGRAM && TG_BOT && (
                    <>
                      {' · '}
                      <a
                        href={`https://t.me/${TG_BOT}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-brand underline-offset-2 hover:underline"
                      >
                        @{TG_BOT}
                      </a>
                    </>
                  )}
                </p>
              )}

              {mutation.isSuccess ? (
                <div className="py-2">
                  <p className="mb-4 flex items-start gap-1.5 text-[14px] font-semibold text-brand">
                    <span aria-hidden>✓</span>
                    {t('support.success')}
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
                  >
                    {t('support.done')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  {topic && (
                    <p
                      className="rounded-md bg-bone px-3 py-2 text-[13px] font-semibold text-ink"
                      data-testid="support-topic"
                    >
                      {t('support.companyData.topic', { tin: topic.tin })}
                    </p>
                  )}
                  <label className="flex flex-col gap-1">
                    <span id={categoryLabelId} className="text-[12px] font-semibold text-ink-soft">
                      {t('support.categoryLabel')}
                    </span>
                    <Select
                      aria-labelledby={categoryLabelId}
                      value={category}
                      onChange={(v) => setCategory(v as SupportCategory)}
                      options={CATEGORIES.map((c) => ({
                        value: c,
                        label: t(`support.category.${c}`),
                      }))}
                      className="h-10 rounded-lg text-[13px]"
                    />
                  </label>

                  <label className="flex flex-col gap-1">
                    <span className="text-[12px] font-semibold text-ink-soft">
                      {t('support.messageLabel')}
                    </span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('support.messagePlaceholder')}
                      rows={4}
                      maxLength={4000}
                      className="w-full resize-y rounded-lg border border-edge bg-bone-surface px-3 py-2 text-[16px] text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none"
                    />
                  </label>

                  {/* Users with a real account inbox submit with it automatically.
                    Guests and Telegram-native users (synthetic, unreachable
                    email) must supply a real reply address. */}
                  {!canReplyToAccount && (
                    <label className="flex flex-col gap-1">
                      <span className="text-[12px] font-semibold text-ink-soft">
                        {t('support.emailLabel')}
                      </span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="h-10 w-full rounded-lg border border-edge bg-bone-surface px-3 text-[16px] text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none"
                      />
                    </label>
                  )}

                  {touched && !valid && (
                    <p className="text-[12.5px] text-status-closing-text" role="alert">
                      {t('support.invalid')}
                    </p>
                  )}
                  {mutation.isError && (
                    <p className="text-[12.5px] text-status-closing-text" role="alert">
                      {t('support.error')}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={mutation.isPending}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid disabled:opacity-60"
                  >
                    {mutation.isPending ? t('support.sending') : t('support.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </RemoveScroll>
      )}
    </>
  );
}
